import { getFolderChunk, getHistory, getOverview, getResultsTablesPage, overviewTableKey } from 'DataDiscovery/discoverApi';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import { topFindingEntity } from 'Secure/Utils';
import { recommendedParamsFor } from 'Secure/valueFormatMapping';

export function findLatestSuccessfulRun(runs = []) {
  return runs.find((run) => run && run.status === 'COMPLETED');
}

// Table-index (PeopleSoft-80K) runs: the inventory builder pages results/tables until
// it hits this many tables, then stops and warns — this is a known practical wall for
// building a masking inventory client-side, not something this pass solves. 2000 is
// comfortably above any pre-index-mode run the UI ever had to handle in one page load.
const INDEX_MODE_INVENTORY_TABLE_CAP = 2000;
const INDEX_MODE_INVENTORY_PAGE_SIZE = 200;

/**
 * Cheap eligibility check: does this data source have at least one COMPLETED
 * discovery run? Answers from run history alone — no overview download.
 */
export async function hasCompletedDiscoverRun(dataSourceId) {
  return Boolean(await latestCompletedDiscoverRun(dataSourceId));
}

/**
 * Latest COMPLETED discovery run for a data source, from run history alone (no overview
 * download) — null when the source has never completed a discovery. Run objects carry
 * `completed_at` / `start_date` as epoch millis.
 */
export async function latestCompletedDiscoverRun(dataSourceId) {
  if (dataSourceId == null) return null;
  const historyRuns = await getHistory(0, 50, 'startedAt,desc', dataSourceId);
  return findLatestSuccessfulRun(historyRuns && historyRuns.runs ? historyRuns.runs : []) || null;
}

/**
 * Overview tables for one run. Folder Search runs persist only the folder INDEX
 * (overview.tables is empty, overview.folders is non-null); their per-file rows —
 * including the sampler_type/delimiter the masking flow needs — live in per-folder
 * chunks, so union every indexed folder's chunk (chunks tile the run). Table-index
 * (PeopleSoft-80K) runs persist only the summary index (overview.tableIndex is
 * non-null); page results/tables?detail=full up to INDEX_MODE_INVENTORY_TABLE_CAP —
 * building an inventory over an unbounded number of tables client-side isn't solved
 * here, so a run past the cap gets a partial inventory plus a visible warning via
 * `opts.onWarning`. Classic runs return their tables as-is.
 *
 * @param {string|number} runId
 * @param {{ onWarning?: (notification: { header: string, body: string }) => void,
 *   onIndexInfo?: (info: { runId: string, totalTables: number, loadedCount: number }) => void }} [opts]
 *   onIndexInfo fires only on the table-index path — hosts use it to enable the
 *   server-search fallback (searchDiscoverIndexTables) when totalTables > loadedCount.
 */
/**
 * Page `results/tables?detail=full` (served LIVE by the worker, never from the platform's
 * stored payload) up to INDEX_MODE_INVENTORY_TABLE_CAP. Returns [] when the endpoint is
 * unavailable so callers can fall back to the stored overview.
 */
async function pageLiveFullTables(runId) {
  const tables = [];
  let page = 0;
  // Bound the loop by pages, not just the table cap, so a page response that never
  // stops signaling "more" can't spin forever.
  const maxPages = Math.ceil(INDEX_MODE_INVENTORY_TABLE_CAP / INDEX_MODE_INVENTORY_PAGE_SIZE) + 1;
  while (tables.length < INDEX_MODE_INVENTORY_TABLE_CAP && page < maxPages) {
    let pageResp = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      pageResp = await getResultsTablesPage(runId, {
        page,
        size: INDEX_MODE_INVENTORY_PAGE_SIZE,
        detail: 'full',
        // Findings-first so a capped load keeps the tables that actually need masking;
        // zero-findings tables are reachable via the server-search fallback.
        sort: 'findings',
        dir: 'desc',
      });
    } catch (e) {
      pageResp = null;
    }
    const items = (pageResp && pageResp.tables) || [];
    if (items.length === 0) break;
    tables.push(...items.slice(0, INDEX_MODE_INVENTORY_TABLE_CAP - tables.length));
    page += 1;
    if (items.length < INDEX_MODE_INVENTORY_PAGE_SIZE) break;
  }
  return tables;
}

async function overviewTablesForRun(runId, opts = {}) {
  const overview = await getOverview(runId);
  if (!overview) return null;
  if (Array.isArray(overview.tables) && overview.tables.length > 0) {
    // Classic runs: the platform serves `overview.tables` from the payload it stored when
    // the run finished, so Profile-sourced field metadata (`data_type`, `key_constraints`)
    // added by a LATER Profile never reaches it. The paged endpoint is computed live by the
    // worker against the data source's current Profile, so prefer it for the inventory and
    // keep the stored tables as the fallback when paging is unavailable.
    const live = await pageLiveFullTables(runId);
    return live.length > 0 ? live : overview.tables;
  }
  if (overview.tableIndex) {
    const totalTables = Number(overview.tableIndex.total_tables) || 0;
    const tables = await pageLiveFullTables(runId);
    if (typeof opts.onIndexInfo === 'function') {
      opts.onIndexInfo({ runId, totalTables, loadedCount: tables.length });
    }
    if (totalTables > INDEX_MODE_INVENTORY_TABLE_CAP && typeof opts.onWarning === 'function') {
      // This is the table-index (relational) path only — file/generic runs return above via
      // overview.folders — so "tables" is the correct noun here and needs no per-source switch.
      opts.onWarning({
        header: 'Large discovery run',
        body: `This source has ${totalTables.toLocaleString()} tables. The inventory `
          + `builder loaded the ${INDEX_MODE_INVENTORY_TABLE_CAP.toLocaleString()} with `
          + 'the most findings; searching checks all tables.',
      });
    }
    return tables;
  }
  if (overview.folders) {
    const chunks = await Promise.all(
      overview.folders.map((f) => getFolderChunk(runId, f.folder_path).catch(() => null))
    );
    const tables = [];
    chunks.filter(Boolean).forEach((c) => tables.push(...(c.tables || [])));
    return tables;
  }
  return Array.isArray(overview.tables) ? overview.tables : null;
}

/**
 * Load discover result tables for a data source via discover-runs API.
 *
 * The latest COMPLETED run is resolved fresh and always wins. `preferredRunId` is only a
 * fallback: callers pin it from navigation state (`prevSearchid` = "latest run when the
 * sources table rendered"), and that snapshot goes stale the moment a discovery re-run
 * completes while the SPA stays mounted — seeding the masking inventory from a superseded
 * run silently hides new findings.
 *
 * @param {number} dataSourceId
 * @param {string} preferredRunId
 * @param {{ onWarning?: (notification: { header: string, body: string }) => void }} [opts]
 *   Passed through to overviewTablesForRun — surfaces the index-mode inventory-cap
 *   warning (see there) via the caller's existing notification mechanism.
 */
export async function loadDiscoverOverviewTables(dataSourceId, preferredRunId, opts = {}) {
  if (dataSourceId == null) return [];
  let latestRunId = null;
  try {
    const historyRuns = await getHistory(0, 50, 'startedAt,desc', dataSourceId);
    const latestRun = findLatestSuccessfulRun(historyRuns && historyRuns.runs ? historyRuns.runs : []);
    latestRunId = latestRun && latestRun.run_id ? latestRun.run_id : null;
  } catch (e) {
    latestRunId = null;
  }
  const candidateRunIds = [...new Set([latestRunId, preferredRunId].filter(Boolean))];
  for (const runId of candidateRunIds) {
    try {
      const tables = await overviewTablesForRun(runId, opts);
      if (tables && tables.length > 0) return tables;
    } catch (e) {
      // fall through to the next candidate
    }
  }
  return [];
}

/**
 * Server-side search across ALL of an index-mode run's tables — the escape hatch for
 * inventories capped at INDEX_MODE_INVENTORY_TABLE_CAP. The worker matches `q` against
 * table and schema names (case-insensitive substring — not field names) and returns
 * full OverviewTableItem rows, the same shape overviewTablesForRun loads, so hosts can
 * merge them straight into selectedFields.
 * One page only: a query matching more than INDEX_MODE_INVENTORY_PAGE_SIZE tables is
 * too broad to act on anyway — totalMatches lets the host say "narrow your search".
 */
export async function searchDiscoverIndexTables(runId, query) {
  const pageResp = await getResultsTablesPage(runId, {
    q: query,
    size: INDEX_MODE_INVENTORY_PAGE_SIZE,
    detail: 'full',
  });
  return {
    tables: (pageResp && pageResp.tables) || [],
    totalMatches: Number(pageResp && pageResp.total_elements) || 0,
  };
}

// The inventory keyspace is the shared overview row key (`schema.table` when the item
// carries a schema). The accordion's splitTableKey and the save path's
// selectedFieldsToInventory both split on the FIRST '.', so the qualified form
// round-trips losslessly. Re-exported so inventory-side callers keep one import home.
export { overviewTableKey };

/**
 * Per-table file-routing metadata from a discover overview: the content-sniffed
 * sampler type (tables[].sampler_type, a DatasourceType name) and per-file delimiter.
 * Keyed by overviewTableKey (= inventory table key). Attached to inventory table objects
 * at save time so masking resolves each file's actual format — a JSON-content .csv is
 * masked with the JSON parser, an unusual delimiter reaches formatOptions.
 */
export function discoverTablesToFileTableMeta(tables = []) {
  const meta = {};
  tables.forEach((table) => {
    const tableName = overviewTableKey(table);
    if (!tableName) return;
    const entry = {};
    if (table.sampler_type) entry.sniffedType = table.sampler_type;
    if (table.delimiter) entry.delimiter = table.delimiter;
    // Fixed-width layout ([{name, position, length}] + record width) — required by the
    // masking service's FIXED_WIDTH parser to overlay masked values at the right offsets.
    if (Array.isArray(table.fixed_width_columns) && table.fixed_width_columns.length > 0) {
      entry.fixedWidthColumns = table.fixed_width_columns;
    }
    if (table.record_length != null) entry.recordLength = table.record_length;
    if (table.fixed_width_has_header != null) entry.fixedWidthHasHeader = table.fixed_width_has_header;
    if (Object.keys(entry).length > 0) meta[tableName] = entry;
  });
  return meta;
}

/** Encrypt inventory UI field shape (FPE_UTF8). */
export function discoverTablesToEncryptSelectedFields(tables = []) {
  const selectedFields = {};
  tables.forEach((table) => {
    const tableName = overviewTableKey(table);
    if (!tableName) return;
    const fields = {};
    (table.fields || []).forEach((field) => {
      const fieldName = field && field.name;
      if (!fieldName) return;
      const findings = field && field.findings ? field.findings : {};
      fields[fieldName] = {
        dataType: field && field.data_type ? String(field.data_type).toLowerCase() : 'string',
        findings,
        checked: Object.keys(findings).length > 0,
        algorithm: 'FPE_UTF8',
        fpeTweak: fieldName,
        fpeKey: '',
      };
    });
    selectedFields[tableName] = fields;
  });
  return selectedFields;
}

/** Masking inventory UI field shape (algorithm from recommendations later). */
/**
 * Worker `key_constraints` (snake_case) → editor shape (camelCase). Null unless the column is
 * under at least one PK / UNIQUE constraint. `enforced` stays null when the engine gave no
 * signal, so the advisory can tell "unknown" from "declared but not enforced".
 */
export function normalizeKeyConstraints(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out = raw
    .filter((k) => k && typeof k === 'object')
    .map((k) => ({
      name: k.name != null ? String(k.name) : '',
      kind: k.kind === 'PRIMARY_KEY' ? 'PRIMARY_KEY' : 'UNIQUE',
      columnCount: Number(k.column_count != null ? k.column_count : k.columnCount) || 1,
      position: Number(k.position) || 1,
      partial: Boolean(k.partial),
      enforced: k.enforced == null ? null : Boolean(k.enforced),
    }));
  return out.length > 0 ? out : null;
}

export function discoverTablesToMaskingSelectedFields(tables = []) {
  const selectedFields = {};
  tables.forEach((table) => {
    const tableName = overviewTableKey(table);
    if (!tableName) return;
    const fields = {};
    (table.fields || []).forEach((field) => {
      const fieldName = field && field.name;
      if (!fieldName) return;
      const findings = field && field.findings ? field.findings : {};
      // Per-finding date-format / name-order distributions from discovery (optional). Carried on
      // the descriptor so the inventory merge can (a) seed the recommended flat params and (b)
      // apply any algorithm-alias override once the entity→algorithm map resolves the base
      // recommendation. `recommendedParams` is precomputed here off the top finding — the same
      // finding type the merge uses to pick `recommendedAlgorithm` — since it depends only on the
      // value_formats block, not the provider map.
      const valueFormats = field && field.value_formats ? field.value_formats : null;
      const recommendedParams = recommendedParamsFor(valueFormats, topFindingEntity(findings));
      // Discovery-only descriptor: not yet in any saved inventory (`saved: false`), so the
      // merge will seed `algorithm` from the recommendation. `recommendedAlgorithm` is filled
      // live from the entity→algorithm map (here '' until a map is available).
      fields[fieldName] = {
        dataType: field && field.data_type ? String(field.data_type).toLowerCase() : 'string',
        findings,
        checked: Object.keys(findings).length > 0,
        algorithm: '',
        recommendedAlgorithm: '',
        params: {},
        recommendedParams,
        valueFormats,
        // PK / UNIQUE membership from the data source's Profile (worker `key_constraints`,
        // snake_case on the wire). Drives the constraint-aware uniqueness advisory; null when
        // the column has none or no Profile exists.
        keyConstraints: normalizeKeyConstraints(field && field.key_constraints),
        saved: false,
      };
    });
    selectedFields[tableName] = fields;
  });
  return selectedFields;
}

/**
 * Same return shape as legacy getSensitiveFields (for MaskDialog / masking inventory fallback).
 */
export async function getSensitiveFieldsFromDiscover(connection, connections) {
  const source = (connections || []).find((c) => c.name === connection);
  const dataSourceId = getDataSourceNumericId(source);
  if (dataSourceId == null) return {};
  const tables = await loadDiscoverOverviewTables(dataSourceId, null);
  return discoverTablesToMaskingSelectedFields(tables);
}
